import type { Bookmark } from '@tanko/shared'

/** 動作以 const tuple 定義，讓說明清單的涵蓋率可以在執行期被斷言。 */
export const readerActions = [
  'forward', 'backward', 'next-page', 'previous-page', 'next-bookmark', 'previous-bookmark',
  'add-bookmark', 'toggle-controls', 'set-cover',
] as const

export type ReaderAction = typeof readerActions[number]

/**
 * 方向鍵會攔截原生捲動，其餘按鍵不攔截。
 *
 * 無修飾的上下鍵是前進與後退一個視窗，不是跳頁：單頁通常比視窗高，跳到下一頁開頭
 * 會直接越過目前這一頁還沒看到的部分。整頁跳頁改掛在 shift 上——那是偶爾用來換一話
 * 的動作，把安全的那一個放在最容易按到的位置。
 */
const actions: Record<string, { action: ReaderAction; preventDefault: boolean }> = {
  ArrowDown: { action: 'forward', preventDefault: true },
  ArrowUp: { action: 'backward', preventDefault: true },
  ArrowRight: { action: 'next-bookmark', preventDefault: true },
  ArrowLeft: { action: 'previous-bookmark', preventDefault: true },
  m: { action: 'add-bookmark', preventDefault: false },
  h: { action: 'toggle-controls', preventDefault: false },
  c: { action: 'set-cover', preventDefault: false },
}

/** 按著 shift 時才有的對應。其餘按鍵加 shift 沿用無修飾的那一份。 */
const shiftActions: Record<string, { action: ReaderAction; preventDefault: boolean }> = {
  ArrowDown: { action: 'next-page', preventDefault: true },
  ArrowUp: { action: 'previous-page', preventDefault: true },
}

/**
 * 按鍵對應到的動作。字母鍵不分大小寫；帶 ctrl／meta／alt 的組合一律不處理，
 * 以免與瀏覽器或作業系統的快捷鍵衝突。shift 只用來區分上下鍵的兩種移動單位。
 */
export function readerShortcut(
  key: string,
  modifiers: { ctrl?: boolean; meta?: boolean; alt?: boolean; shift?: boolean } = {},
): { action: ReaderAction; preventDefault: boolean } | null {
  if (modifiers.ctrl || modifiers.meta || modifiers.alt) return null
  if (modifiers.shift && shiftActions[key]) return shiftActions[key]
  return actions[key] ?? actions[key.toLowerCase()] ?? null
}

/** 焦點在文字輸入控制項或任何開啟的對話框內時，快捷鍵不作用。 */
export function shortcutsBlocked(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return true
  return !!target.closest('[role="dialog"], [role="alertdialog"]')
}

/**
 * 目前頁前後最近的書籤頁碼，到端點時繞到另一端。
 *
 * 書籤的用途就是在一本作品裡來回跳，停在端點會讓最後那段路得用手捲。同一頁上的書籤
 * 在兩個方向都不算目的地——跳到自己身上看起來像壞掉——因此整本只有一個書籤而且就在
 * 目前頁時兩個方向都回傳 null，循環不會變成原地跳。
 */
export function nextBookmarkPage(
  bookmarks: readonly Pick<Bookmark, 'page'>[],
  currentPage: number,
  direction: 'next' | 'previous',
): number | null {
  const pages = bookmarks.map(bookmark => bookmark.page).filter(page => page !== currentPage)
  if (!pages.length) return null
  if (direction === 'next') {
    const ahead = pages.filter(page => page > currentPage)
    return ahead.length ? Math.min(...ahead) : Math.min(...pages)
  }
  const behind = pages.filter(page => page < currentPage)
  return behind.length ? Math.max(...behind) : Math.max(...pages)
}

/** 快捷鍵說明清單的一筆：實際按鍵、畫面上顯示的字樣、動作、與中文說明。 */
export type ShortcutHelpEntry = {
  key: string
  /** 需要按著 shift 才會得到這個動作。 */
  shift?: boolean
  keyLabel: string
  action: ReaderAction
  description: string
}

/**
 * 快捷鍵說明清單。與按鍵對應表放在同一個模組，且每一筆都帶實際按鍵，
 * 因此單元測試可以直接以 readerShortcut 驗證說明與實際行為一致，
 * 兩者不會各自漂移。
 */
export const shortcutHelp: readonly ShortcutHelpEntry[] = [
  { key: 'ArrowDown', keyLabel: '↓', action: 'forward', description: '往下一個畫面' },
  { key: 'ArrowUp', keyLabel: '↑', action: 'backward', description: '往上一個畫面' },
  { key: 'ArrowDown', shift: true, keyLabel: '⇧↓', action: 'next-page', description: '下一頁' },
  { key: 'ArrowUp', shift: true, keyLabel: '⇧↑', action: 'previous-page', description: '上一頁' },
  { key: 'ArrowRight', keyLabel: '→', action: 'next-bookmark', description: '下一個書籤（到底繞回第一個）' },
  { key: 'ArrowLeft', keyLabel: '←', action: 'previous-bookmark', description: '上一個書籤（到頂繞到最後一個）' },
  { key: 'm', keyLabel: 'M', action: 'add-bookmark', description: '在目前頁加入書籤' },
  { key: 'h', keyLabel: 'H', action: 'toggle-controls', description: '切換工具列' },
  { key: 'c', keyLabel: 'C', action: 'set-cover', description: '將目前頁設為封面' },
]
